import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText, ClipboardList, BookOpen, FlaskConical,
  ArrowRight, Check, ChevronRight
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useUIStore } from '../../store/uiStore'
import { supabase } from '../../lib/supabase'
import { listTheses } from '../../lib/thesisApi'
import { programmeLabel } from '../../utils/programme'

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

const JOURNEY = [
  { label: 'Profile',    key: 'profile' },
  { label: 'SOP',        key: 'sop' },
  { label: 'Synopsis',   key: 'synopsis' },
  { label: 'Thesis ToC', key: 'toc' },
  { label: 'Writing',    key: 'writing' },
  { label: 'Submission', key: 'submission' },
]

const ACTIONS = [
  { icon: FileText,      title: 'Write my SOP',      desc: 'Statement of Purpose, 600–800 words, ready in 60 seconds', path: '/app/documents/sop',            color: '#2563eb', bg: '#eff6ff' },
  { icon: ClipboardList, title: 'Build Synopsis',     desc: 'Full research proposal with objectives, methodology and references',path: '/app/documents/synopsis',   color: '#7c3aed', bg: '#faf5ff' },
  { icon: BookOpen,      title: 'Start My Thesis',    desc: 'Table of Contents first, then chapter by chapter',             path: '/app/thesis',               color: '#2563eb', bg: 'rgba(37,99,235,0.1)' },
  { icon: FlaskConical,  title: 'Research Assistant', desc: 'Ask academic questions, find frameworks, analyse literature',   path: '/app/research',             color: '#16a34a', bg: '#f0fdf4' },
]

const STATUS_STYLE = {
  COMPLETE:   { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  DRAFT:      { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  GENERATING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
}

export default function DashboardPage() {
  const { user, profile } = useAuthStore()
  const { setTotalWords }  = useUIStore()

  const [stats, setStats]   = useState({ documents: [], theses: [], totalWords: 0 })
  const [journey, setJourney] = useState({ profile: true, sop: false, synopsis: false, toc: false, writing: false, submission: false })

  const firstName = (profile?.full_name || user?.email || '').split(' ')[0]

  useEffect(() => {
    const load = async () => {
      try {
        const [docsRes, thesesRes] = await Promise.allSettled([
          supabase.from('documents').select('id, type, word_count, status').eq('user_id', user?.id),
          listTheses(user?.id),
        ])
        const docs   = docsRes.status   === 'fulfilled' ? (docsRes.value.data  || []) : []
        const theses = thesesRes.status === 'fulfilled' ? (thesesRes.value     || []) : []
        const words  = [...docs.map(d => d.word_count || 0), ...theses.map(t => t.total_word_count || 0)].reduce((a, b) => a + b, 0)
        setStats({ documents: docs, theses, totalWords: words })
        setTotalWords(words)
        setJourney({
          profile:    true,
          sop:        docs.some(d => d.type === 'SOP'      && d.status === 'COMPLETE'),
          synopsis:   docs.some(d => d.type === 'SYNOPSIS' && d.status === 'COMPLETE'),
          toc:        theses.some(t => t.toc_finalised || t.tocFinalised),
          writing:    theses.some(t => (t.total_word_count || 0) > 0),
          submission: false,
        })
      } catch {}
    }
    load()
  }, [setTotalWords])

  const currentJourneyStep = JOURNEY.findIndex(s => !journey[s.key])

  return (
    <div className="space-y-6 max-w-6xl">

      {/* Welcome */}
      <div>
        <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2.2rem', lineHeight: 1.2, color: '#0f172a', fontWeight: 700 }}>
          {greeting()}, {firstName}
        </h1>
        {(profile?.university || profile?.programme || profile?.domain) && (
          <p className="mt-2 text-sm flex items-center gap-2 flex-wrap" style={{ color: '#64748b' }}>
            {[profile.university, profile.programme, profile.domain].filter(Boolean).map((v, i, arr) => (
              <span key={v} className="flex items-center gap-2">
                <span className="font-mono text-xs px-2 py-0.5 rounded-md"
                  style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>{v}</span>
                {i < arr.length - 1 && <span style={{ color: '#e2e8f0' }}>·</span>}
              </span>
            ))}
          </p>
        )}
      </div>

      {/* Journey stepper */}
      <div className="rounded-2xl p-6" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <p className="font-mono mb-6" style={{ fontSize: 10, letterSpacing: '0.12em', color: '#94a3b8' }}>{programmeLabel(profile?.programme).toUpperCase()} JOURNEY</p>
        <div className="relative flex items-start justify-between">
          {/* Connector lines */}
          <div className="absolute top-4 left-0 right-0 flex" style={{ zIndex: 0 }}>
            {JOURNEY.slice(0, -1).map((s, i) => (
              <div key={s.key} className="flex-1 h-px"
                style={{ background: journey[JOURNEY[i + 1].key] ? '#2563eb' : '#e2e8f0', marginTop: '-0.5px' }} />
            ))}
          </div>

          {JOURNEY.map(({ label, key }, i) => {
            const done    = journey[key]
            const current = i === currentJourneyStep
            return (
              <div key={key} className="flex flex-col items-center gap-2 relative z-10" style={{ flex: 1 }}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    background: done ? '#2563eb' : '#ffffff',
                    border:     done ? 'none' : current ? '2px solid #2563eb' : '2px solid #e2e8f0',
                    outline:    current ? '4px solid rgba(37,99,235,0.12)' : 'none',
                    outlineOffset: '2px',
                    boxShadow:  done ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                  }}
                >
                  {done
                    ? <Check style={{ width: 13, height: 13, color: '#ffffff', strokeWidth: 3 }} />
                    : <span className="font-mono text-xs" style={{ color: current ? '#2563eb' : '#94a3b8' }}>{i + 1}</span>
                  }
                </div>
                <span className="font-sans text-xs text-center"
                  style={{ color: done ? '#2563eb' : current ? '#0f172a' : '#94a3b8', fontWeight: current ? 600 : 400 }}>
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="font-sans font-semibold text-sm mb-4" style={{ color: '#0f172a' }}>Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ACTIONS.map(({ icon: Icon, title, desc, path, color, bg }) => (
            <Link
              key={path}
              to={path}
              className="group flex items-start gap-4 p-5 rounded-2xl transition-all duration-200"
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(15,23,42,0.08)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.06)' }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
                <Icon style={{ width: 18, height: 18, color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm mb-0.5" style={{ color: '#0f172a' }}>{title}</p>
                <p className="font-sans text-xs leading-relaxed" style={{ color: '#94a3b8' }}>{desc}</p>
              </div>
              <ArrowRight className="shrink-0 mt-1 transition-transform group-hover:translate-x-0.5"
                style={{ width: 14, height: 14, color: '#94a3b8' }} />
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Documents */}
      <div className="rounded-2xl" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>Recent Documents</h2>
        </div>
        {stats.documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText style={{ width: 32, height: 32, color: '#94a3b8', margin: '0 auto 12px', opacity: 0.5 }} />
            <p className="font-sans text-sm font-medium mb-1" style={{ color: '#475569' }}>No documents yet</p>
            <p className="font-sans text-xs" style={{ color: '#94a3b8' }}>Use the Quick Actions above to get started.</p>
          </div>
        ) : (
          <div>
            {stats.documents.slice(0, 5).map((doc) => {
              const s = STATUS_STYLE[doc.status] || STATUS_STYLE.DRAFT
              return (
                <div key={doc.id} className="flex items-center gap-4 px-6 py-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <div className="px-2 py-1 rounded-md text-xs font-mono font-medium shrink-0"
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {doc.type}
                  </div>
                  <span className="flex-1 font-sans text-sm truncate" style={{ color: '#0f172a' }}>{doc.title}</span>
                  <span className="font-mono text-xs shrink-0" style={{ color: '#94a3b8' }}>
                    {(doc.word_count || 0).toLocaleString()} words
                  </span>
                  <span className="px-2 py-0.5 rounded-md text-xs font-sans shrink-0"
                    style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                    {doc.status}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Thesis progress */}
      {stats.theses.map(thesis => {
        const chapters = thesis.chapters || []
        const total    = chapters.length || 1
        const done     = chapters.filter(c => c.status === 'LOCKED' || c.status === 'REVIEWED').length
        const words    = thesis.total_word_count || 0
        const pages    = thesis.estimated_pages  || 0

        return (
          <div key={thesis.id} className="rounded-2xl p-6" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '1.15rem', color: '#0f172a', fontWeight: 600 }}>
                  {thesis.title}
                </h2>
                <p className="font-sans text-xs mt-1" style={{ color: '#94a3b8' }}>{programmeLabel(thesis.programme || profile?.programme)} Thesis</p>
              </div>
              <Link
                to={`/app/thesis/${thesis.id}/chapters`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-sans text-sm font-semibold transition-all"
                style={{ background: '#2563eb', color: '#ffffff' }}
                onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
                onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
              >
                Continue Writing <ChevronRight style={{ width: 14, height: 14 }} />
              </Link>
            </div>

            {chapters.length > 0 && (
              <div className="flex gap-1 mb-5">
                {chapters.map(ch => {
                  const c = ch.status === 'LOCKED' || ch.status === 'REVIEWED' ? '#22c55e'
                           : ch.status === 'DRAFT' || ch.status === 'GENERATING' ? '#2563eb'
                           : '#e2e8f0'
                  return <div key={ch.id} className="flex-1 h-1.5 rounded-full" style={{ background: c }}
                    title={`Ch ${ch.chapter_number}: ${ch.status}`} />
                })}
              </div>
            )}

            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Total Words',    value: words.toLocaleString() },
                { label: 'Pages Written',  value: pages },
                { label: 'Chapters Done',  value: `${done} / ${total}` },
                { label: '% to 200 pages', value: `${Math.min(Math.round((pages / 200) * 100), 100)}%` },
              ].map(({ label, value }) => (
                <div key={label} className="text-center p-3 rounded-xl" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <p className="font-mono text-lg font-semibold" style={{ color: '#2563eb' }}>{value}</p>
                  <p className="font-sans text-xs mt-0.5" style={{ color: '#94a3b8' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
